import styled from "@emotion/styled";
import React from "react";
import { compassColors } from "src/utils/styling";
import ArrowRightIcon from "./images/arrow-right.svg";
import i18n from "src/locales";
import Dropdown from "@components/dropdown/Dropdown";
import { fontTitle, overline } from "src/design-system/styles/fonts";
import { breakpointNavBarFull } from "../nav-bar/Hamburger";
import { trackEvent } from "src/utils/api/tracker";
import { css } from "@emotion/react";
import ArrowDownIcon from "./images/arrowDown.svg";

type ClinicalNavBarDropdownProps = {
  menuOpen: boolean;
};

export const NavBarItem = styled("a", {
  shouldForwardProp: (prop) => prop !== "isMobileView",
})<{ isMobileView?: boolean }>`
  ${fontTitle}
  text-decoration: none;
  text-transform: uppercase;
  margin: 10px 0;
  color: ${compassColors.lagoon};
  ${({ isMobileView }) =>
    isMobileView &&
    css`
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
    `}
`;

const SubMenuContent = styled.div`
  display: none;
  opacity: 0;
  visibility: hidden;
  transition: visibility 0.5s;
  margin-bottom: 0;
  margin-top: 15px;
  width: 100%;
  border-bottom: 1px solid ${compassColors.sand1};

  ${NavBarItem} {
    &:last-child {
      margin-bottom: 20px;
    }
  }
`;

const ArrowImage = styled.img`
  margin: 0;
  padding-left: 5px;
`;

const ExpandingNavItemText = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
`;

const ExpandingNavItem = styled.div`
  ${fontTitle}
  text-decoration: none;
  text-transform: uppercase;
  margin: 10px 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  &:focus,
  &:focus-within {
    ${SubMenuContent} {
      opacity: 1;
      visibility: visible;
      pointer-events: auto;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
    }
    & ${ExpandingNavItemText} ${ArrowImage} {
      transform: rotate(180deg);
    }
  }
  @media ${breakpointNavBarFull} {
    ${fontTitle}
  }
`;

const NavDropdown = styled(NavBarItem)`
  ${overline}
  text-decoration: none;
  font-size: 28px;
  line-height: 36px;
  text-transform: uppercase;
  margin: 10px 0;
  @media ${breakpointNavBarFull} {
    ${fontTitle}
    text-decoration: none;
    margin: 0 14px;
    color: ${compassColors.white};
    display: inline-block;

    & div {
      background-color: ${compassColors.white};
      border-radius: 16px;
      width: 343px;
      padding-left: 16px;
    }
  }
`.withComponent(Dropdown);

export function ClinicalNavBarDropdown({
  menuOpen,
}: ClinicalNavBarDropdownProps) {
  const linksConfig = [
    {
      link: "https://www.noom.com/for-work/?nfw_section=employers",
      text: "navBar:employers",
      trackingParam: "ClickedEmployersNavLink",
    },
    {
      link: "https://www.noom.com/for-work/?nfw_section=healthPlans",
      text: "navBar:healthPlans",
      trackingParam: "ClickedHealthPlansNavLink",
    },
    {
      link: "https://www.noom.com/for-work/?nfw_section=consultantsAndBrokers",
      text: "navBar:consultantsBrokers",
      trackingParam: "ClickedConsultantsAndBrokersNavLink",
    },
    {
      link: "https://www.noom.com/health-systems",
      text: "navBar:healthSystems",
      trackingParam: "ClickedHealthSystemsNavLink",
    },
    {
      link: "https://www.noom.com/for-work/resources",
      text: "navBar:resources",
      trackingParam: "ClickedResourcesNavLink",
    },
  ];

  return menuOpen ? (
    <ExpandingNavItem
      tabIndex={0}
      onClick={() => trackEvent("ClickedForOrganizationsNavLink")}
    >
      <ExpandingNavItemText>
        {i18n.t("navBar:forOrganizations")}
        <ArrowImage src={ArrowDownIcon} />
      </ExpandingNavItemText>
      <SubMenuContent>
        {linksConfig.map((menuItem) => (
          <NavBarItem
            isMobileView
            key={menuItem.link}
            href={menuItem.link}
            onClick={() => trackEvent(menuItem.trackingParam)}
          >
            {i18n.t(menuItem.text)}
            {menuOpen && <ArrowImage src={ArrowRightIcon} />}
          </NavBarItem>
        ))}
      </SubMenuContent>
    </ExpandingNavItem>
  ) : (
    <NavDropdown
      title={i18n.t("navBar:forOrganizations")}
      onClick={() => trackEvent("ClickedForOrganizationsNavLink")}
    >
      {linksConfig.map((menuItem) => (
        <NavBarItem
          key={menuItem.link}
          href={menuItem.link}
          onClick={() => trackEvent(menuItem.trackingParam)}
        >
          {i18n.t(menuItem.text)}
          {menuOpen && <ArrowImage src={ArrowRightIcon} />}
        </NavBarItem>
      ))}
    </NavDropdown>
  );
}
